import * as THREE from 'three/webgpu';
import {isAbstractSketchEdgeObject} from '../guards/isAbstractSketchEdgeObject';
import {isSketchPointObject} from '../guards/isSketchPointObject';
import {isSketchContour} from '../meshProxy/SketchContour';

/**
 * 草图前景渲染状态应用器。
 *
 * 职责：
 * 1. 将草图对象树统一提升到实体宿主面前景层，避免共面绘制时被宿主面遮挡；
 * 2. 统一关闭草图对象材质的深度测试与深度写入，避免进入实体面草图后出现 Z-Fighting；
 * 3. 仅处理草图对象树的渲染状态，不参与几何构建、选择策略或草图业务流程。
 */
export class SketchOverlayRenderStateApplier {
    /** 草图轮廓填充的最小渲染层级。 */
    public static readonly CONTOUR_RENDER_ORDER = 180;

    /** 草图边对象的最小渲染层级。 */
    public static readonly EDGE_RENDER_ORDER = 200;

    /** 草图点对象的最小渲染层级。 */
    public static readonly POINT_RENDER_ORDER = 210;

    /**
     * 命令待确认预览线的最小渲染层级。
     *
     * 说明：
     * 1. 草图编辑预览点当前最高层级为 620，命令预览线必须位于普通草图边、
     *    草图点和绘制预览点之上，避免被共面的实体面或草图边遮挡；
     * 2. 草图圆角预览与等距实体预览统一引用本层级，避免散落魔法数；
     * 3. 该层级只服务临时预览对象，不影响正式草图实体的交互层级。
     */
    public static readonly COMMAND_PREVIEW_RENDER_ORDER = 650;

    /**
     * 对草图对象树应用统一的前景渲染状态。
     *
     * 说明：
     * 1. 这里按对象树整体遍历，确保复合草图对象内部的子对象也能继承相同规则；
     * 2. 只提升最小 renderOrder，不覆盖已经更高的调试/交互层；
     * 3. 材质统一关闭深度测试与深度写入，确保与草图宿主面共面时仍可见。
     *
     * @param root 需要应用前景渲染状态的根对象。
     */
    public apply(root: THREE.Object3D): void {
        root.traverse((object) => {
            this.applyToObject(object);
        });
    }

    /**
     * 按草图对象类型分发对应的渲染状态。
     *
     * @param object 当前遍历到的对象。
     */
    private applyToObject(object: THREE.Object3D): void {
        if (isSketchPointObject(object) && object instanceof THREE.Sprite) {
            this.applyPointRenderState(object);
            return;
        }

        if (isAbstractSketchEdgeObject(object) && object instanceof THREE.Line) {
            this.applyEdgeRenderState(object);
            return;
        }

        if (isSketchContour(object)) {
            this.applyContourRenderState(object as unknown as THREE.Mesh);
        }
    }

    /**
     * 应用草图点对象的前景渲染状态。
     *
     * @param point 草图点对象。
     */
    private applyPointRenderState(point: THREE.Sprite): void {
        point.renderOrder = Math.max(point.renderOrder, SketchOverlayRenderStateApplier.POINT_RENDER_ORDER);
        this.applyMaterialDepthState(point.material);
    }

    /**
     * 应用草图边对象的前景渲染状态。
     *
     * @param edge 草图边对象。
     */
    private applyEdgeRenderState(edge: THREE.Line): void {
        edge.renderOrder = Math.max(edge.renderOrder, SketchOverlayRenderStateApplier.EDGE_RENDER_ORDER);
        this.applyMaterialDepthState(edge.material);
    }

    /**
     * 应用草图轮廓填充对象的前景渲染状态。
     *
     * @param contour 草图轮廓填充对象。
     */
    private applyContourRenderState(contour: THREE.Mesh): void {
        contour.renderOrder = Math.max(contour.renderOrder, SketchOverlayRenderStateApplier.CONTOUR_RENDER_ORDER);
        this.applyMaterialDepthState(contour.material);
    }

    /**
     * 对材质统一关闭深度测试与深度写入。
     *
     * 说明：
     * 1. 草图宿主面与草图对象大概率共面，若保留默认深度行为会产生闪烁或直接不可见；
     * 2. 这里不修改颜色、透明度等业务样式，只处理宿主面遮挡相关的深度状态；
     * 3. 同时兼容单材质和多材质对象，避免复合对象遗漏。
     *
     * @param material 草图对象材质或材质数组。
     */
    private applyMaterialDepthState(material: THREE.Material | THREE.Material[] | null | undefined): void {
        const materials = Array.isArray(material)
            ? material
            : material
                ? [material]
                : [];

        materials.forEach((currentMaterial) => {
            currentMaterial.depthTest = false;
            currentMaterial.depthWrite = false;
            currentMaterial.needsUpdate = true;
        });
    }
}
